import { useState, useRef} from "react";
import { useSearchParams } from 'react-router-dom';
import { FluentProvider } from "@fluentui/react-components";
import { duploLightTheme, Label, TextStyle, FontColor } from "@lannister/duplo";

import LoadingView from "../loadingView";
import PaymentMethods from "../Checkout/paymentMethods";
import usePaymentLink from "./usePaymentLink";
import { PaymentMethodType, Currency } from "../../common/constants";
import UTILS from "../../common/utils";
import InvoiceDetails from "./invoiceDetails";
import RequiredDetails from "./requiredDetails";
import "./styles.css";
import useApplePay from "../../hooks/apple-pay";
import AuthenticationView from "../AuthenticationView";

const PaymentLinkView = () => {
    const selectedPaymentMethodRef = useRef(PaymentMethodType.BankCard);
    const [isPaymentDataValid, setIsPaymentDataValid] = useState(false);
    const [isRequiredFieldsFilled, setIsRequiredFieldsFilled] = useState(false);
    const paymentDataRef = useRef({});
    const customerDataRef = useRef({});
    const isApplePayAvailable = useApplePay();

    const getProfileName = () => {
        let paths = window.location.pathname.split("/");
        let profileName = paths.pop();
        while (profileName === '') {
            profileName = paths.pop();
        }

        return profileName;
    }

    const getMerchantName = () => {
        return profileName.split('_')[0];
    }

    const getMerchantId = () => {
        return profileName.split('_')[1];
    }

    const [searchParams] = useSearchParams();
    const orderRef = searchParams.get('order');
    const profileName = getProfileName();
    const merchantId = getMerchantId();

    const {
        isLoading,
        error,
        merchantLogo,
        isPaying,
        paymentLinkDetails,
        amount,
        showAuthView,
        authViewHtml,
        authViewUrl,
        initiatePayment,
        submitPayment,
        updateMetadata } = usePaymentLink(profileName, orderRef, merchantId);

    const onSubmit = async () => {

        const authToken = await initiatePayment(selectedPaymentMethodRef.current);
        if (!authToken) {
            return;
        }
        const metadataUpdated = await updateMetadata(authToken, customerDataRef.current);

        if (metadataUpdated === true) {
            await submitPayment(authToken, paymentDataRef.current, selectedPaymentMethodRef.current, getMerchantName());
        } 
    }

    const handlePaymentDataChange = (field, value) => {
        paymentDataRef.current[field] = value;
        setIsPaymentDataValid(UTILS.ValidateFormData(paymentDataRef.current, selectedPaymentMethodRef.current));
    };

    const handleCustomerDataChange = (field, value) => {
        customerDataRef.current[field] = value;
    }

    const onChangeSelectedPaymentMethod = (method) => {
        selectedPaymentMethodRef.current = method;
        paymentDataRef.current = {};
        setIsPaymentDataValid(UTILS.ValidateFormData(paymentDataRef.current, selectedPaymentMethodRef.current));
    };

    const isFormValid = () => {
        if (paymentLinkDetails.is_product_page_enabled === true && paymentLinkDetails.is_checkout_form_enabled === true)
            return isPaymentDataValid && isRequiredFieldsFilled
        return isPaymentDataValid;
    }

    const SectionDivider = () => (
        <div style={{ height: 1, backgroundColor: "#E6E8FA", alignSelf: "stretch" }} />
    );

    const getContainerClassName = () => {
        if (!paymentLinkDetails.is_product_page_enabled) return 'page-container stacked';
        return 'page-container';
    }


    if (isLoading) return (<LoadingView color='#4055FF'/>);
    if (error) return (<Label labelText={error} textStyle={TextStyle.Heading5Regular} textColor={FontColor.Error} />);
    if (showAuthView) {
        return (
            <AuthenticationView
                paymentMethodType={selectedPaymentMethodRef.current}
                authViewHtml={authViewHtml}
                authViewUrl={authViewUrl} />);
    }

    return (
        <FluentProvider theme={duploLightTheme}>
            <div className={getContainerClassName()}>
                <div className="left-panel">
                    <div className="invoice-header">
                        <div className="title">
                            { merchantLogo !== null && <img src={merchantLogo} className="merchant-logo" alt="merchant" /> }
                            <div className="merchant-text">
                                <Label labelText={paymentLinkDetails.title} textStyle={TextStyle.Heading5Regular} />
                                <Label
                                    labelText={`${amount} ${Currency.EGP}`}
                                    textStyle={TextStyle.Heading3Semibold} />
                            </div>
                        </div>
                        <SectionDivider />
                        <Label labelText={paymentLinkDetails.description} textStyle={TextStyle.BaseRegular}/>
                    </div>

                    {paymentLinkDetails.is_product_page_enabled === true &&
                        <>
                            {paymentLinkDetails.is_checkout_form_enabled === true &&
                                <RequiredDetails
                                    onDataChange={handleCustomerDataChange}
                                    schema={paymentLinkDetails.merchant_checkout_form_schema.schema}
                                    setIsValid={setIsRequiredFieldsFilled} />}

                            <InvoiceDetails
                                invoiceId={paymentLinkDetails.merchant_reference ?? orderRef}
                                expirationDate={paymentLinkDetails.expires_at}
                                extraDetails={paymentLinkDetails.extra_custom_details} />
                        </>}
                </div>

                <div className="right-panel">
                    <PaymentMethods
                        isCardEnabled={paymentLinkDetails.merchant_config.is_bank_card_enabled}
                        isWalletEnabled={paymentLinkDetails.merchant_config.is_wallet_enabled}
                        isApplePayEnabled={paymentLinkDetails.merchant_config.is_apple_pay_enabled && isApplePayAvailable}
                        handleChange={handlePaymentDataChange}
                        onSubmit={onSubmit}
                        isLoading={isPaying}
                        onChangeSelectedPaymentMethod={onChangeSelectedPaymentMethod}
                        isFormValid={isFormValid()}
                        amount={amount}
                        currency={Currency.EGP}
                        shouldShowAmount={paymentLinkDetails.merchant_config.show_paid_amount}
                        isSaveCardOptionEnabled={paymentLinkDetails.save_card_checkbox_enabled} />
                </div>
            </div>
        </FluentProvider>
    );
}

export default PaymentLinkView;
